import React, { useState, useRef, useCallback, useEffect } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { Route as RouteIcon, Plus, Eye, X, MapPin, Flag } from 'lucide-react';
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import CrossingStopsView from './CrossingStopsView.js';

const PATH_PREVIEW_MAX = 48;

const hubLabel = (hub) => {
  if (!hub?.location) return null;
  return hub.location_code ? `${hub.location} (${hub.location_code})` : hub.location;
};

const formatPath = (route) => {
  const parts = [];
  if (route.from_location?.location) parts.push(route.from_location.location);
  (route.via_locations || []).forEach((h) => {
    if (h.location) parts.push(h.location);
  });
  if (route.to_location?.location) parts.push(route.to_location.location);
  return parts.join(' → ') || '—';
};

/** Ordered stops for visual track: From → Via… → To */
const getRouteStops = (route) => {
  if (!route) return [];
  const stops = [];
  if (route.from_location?.location) {
    stops.push({
      name: hubLabel(route.from_location),
      role: 'from',
      roleLabel: 'Origin',
    });
  }
  (route.via_locations || []).forEach((h, i) => {
    if (h.location) {
      stops.push({
        name: hubLabel(h),
        role: 'via',
        roleLabel: `Via ${i + 1}`,
      });
    }
  });
  if (route.to_location?.location) {
    stops.push({
      name: hubLabel(route.to_location),
      role: 'to',
      roleLabel: 'Destination',
    });
  }
  return stops;
};

/** Preview + orange eye → visual route track modal */
function RoutePathCell({ path, onViewFull, singleLine = false }) {
  const text = path?.trim() || '';
  if (!text || text === '—') {
    return <span className="text-sm text-gray-500">—</span>;
  }

  const isLong = text.length > PATH_PREVIEW_MAX;
  const preview = isLong ? `${text.slice(0, PATH_PREVIEW_MAX).trimEnd()}…` : text;

  return (
    <div
      className={`flex min-w-0 gap-1.5 ${
        singleLine ? 'items-center w-full' : 'items-start max-w-xs lg:max-w-sm'
      }`}
    >
      <span
        className={`flex-1 min-w-0 ${
          singleLine
            ? 'truncate text-xs sm:text-sm text-gray-900'
            : 'text-sm text-gray-600 leading-snug line-clamp-2 break-words'
        }`}
      >
        {preview}
      </span>
      <button
        type="button"
        onClick={onViewFull}
        className="shrink-0 p-1 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors"
        title="View route track"
        aria-label="View route track"
      >
        <Eye className="w-4 h-4" />
      </button>
    </div>
  );
}

function RouteTrackModal({ route, onClose }) {
  const stops = getRouteStops(route);
  const title = route?.name || route?.route_id || 'Route Path';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-3 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="route-path-modal-title"
      >
        <div className="flex items-center justify-between gap-2 px-3.5 sm:px-4 pt-3 pb-2.5 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h3 id="route-path-modal-title" className="text-sm sm:text-base font-semibold text-gray-800 truncate">
              Route Track
            </h3>
            <p className="text-[11px] text-gray-500 truncate">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        <div className="px-3.5 sm:px-4 py-3 overflow-y-auto flex-1 min-h-0">
          {stops.length === 0 ? (
            <p className="text-sm text-gray-500">No path available</p>
          ) : (
            <div className="relative">
              {stops.map((stop, index) => {
                const isFirst = index === 0;
                const isLast = index === stops.length - 1;
                const tone = isFirst
                  ? { dot: 'bg-sky-500', ring: 'ring-sky-100', badge: 'bg-sky-50 text-sky-700 border-sky-100', line: 'bg-sky-200' }
                  : isLast
                    ? { dot: 'bg-emerald-500', ring: 'ring-emerald-100', badge: 'bg-emerald-50 text-emerald-700 border-emerald-100', line: 'bg-emerald-200' }
                    : { dot: 'bg-orange-400', ring: 'ring-orange-100', badge: 'bg-orange-50 text-orange-700 border-orange-100', line: 'bg-orange-200' };

                return (
                  <div key={`${stop.role}-${index}`} className="flex gap-2.5 min-w-0">
                    <div className="flex flex-col items-center shrink-0 w-5">
                      <div
                        className={`w-5 h-5 rounded-full ${tone.dot} ring-2 ${tone.ring} flex items-center justify-center text-white shrink-0`}
                      >
                        {isLast ? (
                          <Flag className="w-2.5 h-2.5" />
                        ) : (
                          <MapPin className="w-2.5 h-2.5" />
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 flex-1 min-h-[10px] my-0.5 rounded-full ${tone.line}`} />
                      )}
                    </div>

                    <div className={`flex-1 min-w-0 ${isLast ? 'pb-0' : 'pb-2'}`}>
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span
                          className={`inline-flex items-center px-1.5 py-px rounded text-[9px] font-semibold border ${tone.badge} shrink-0`}
                        >
                          {stop.roleLabel}
                        </span>
                        <span className="text-[10px] text-gray-400 shrink-0">
                          {index + 1}/{stops.length}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm font-semibold text-gray-900 break-words leading-snug mt-0.5">
                        {stop.name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-3.5 sm:px-4 py-2.5 border-t border-gray-100 flex justify-end shrink-0">
          <Button variant="secondary" type="button" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function CrossingLocationCell({ route }) {
  const stops = route?.crossing_stops || [];
  if (!stops.length) {
    return <span className="text-sm text-gray-500">—</span>;
  }
  return (
    <div className="min-w-[180px] max-w-sm">
      <CrossingStopsView stops={stops} emptyLabel="—" />
    </div>
  );
}

const RouteList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromHubId, setFromHubId] = useState('');
  const [toHubId, setToHubId] = useState('');
  const [crossingHubId, setCrossingHubId] = useState('');
  const [viaHubId, setViaHubId] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewPathRoute, setViewPathRoute] = useState(null);
  const dropdownRef = useRef(null);
  const perPage = 10;

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (fromHubId) filters.from_location_hub_id = fromHubId;
      if (toHubId) filters.to_location_hub_id = toHubId;
      if (crossingHubId) filters.crossing_hub_id = crossingHubId;
      if (viaHubId) filters.via_location_hub_id = viaHubId;
      const response = await routeService.getAll(currentPage, perPage, filters);
      setRoutes((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
      setTotalPages(response.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load routes');
      console.error(err);
      if (currentPage <= 1) setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, [search, fromHubId, toHubId, crossingHubId, viaHubId, currentPage, perPage]);

  useEffect(() => {
    const loadHubs = async () => {
      try {
        const res = await locationHubService.getAllHubs(1, 1000, { all: 'true' });
        const list = res.data?.hubs || res.data?.data || res.data || [];
        setHubs(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
      }
    };
    loadHubs();
  }, []);

  usePaginatedListFetch({
    fetchPage: fetchRoutes,
    setCurrentPage,
    currentPage,
    resetPageDeps: [search, fromHubId, toHubId, crossingHubId, viaHubId],
    debounceWhen: search,
    debounceMs: 0,
  });

  const handleDelete = async (routeId, routeName) => {
    setActiveDropdown(null);
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${routeName}"? ML sheets will fall back to unsorted order if no route matches.`,
      'Delete Route'
    );
    if (!confirmed) return;

    try {
      await routeService.delete(routeId);
      Alert.success('Route deleted successfully!');
      if (routes.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchRoutes();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete route');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFromHubId('');
    setToHubId('');
    setCrossingHubId('');
    setViaHubId('');
    setCurrentPage(1);
  };

  const renderHubOptions = () => hubs.map((hub) => (
    <option key={hub.id} value={hub.id}>
      {hub.location}{hub.location_code ? ` (${hub.location_code})` : ''}
    </option>
  ));

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <ListPageHeader title="Routes" icon={RouteIcon} className="mb-4 sm:mb-4">
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/routes/add')}>
            Add Route
          </Button>
        </ListPageHeader>

        <ListFilterPanel
          hasActiveFilters={!!(search || fromHubId || toHubId || crossingHubId || viaHubId)}
          onClearFilters={clearFilters}
        >
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              {t('common.search')}
            </label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Route name or ID..."
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              From
            </label>
            <select value={fromHubId} onChange={(e) => setFromHubId(e.target.value)}>
              <option value="">All origins</option>
              {renderHubOptions()}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              To
            </label>
            <select value={toHubId} onChange={(e) => setToHubId(e.target.value)}>
              <option value="">All destinations</option>
              {renderHubOptions()}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Crossing location
            </label>
            <select value={crossingHubId} onChange={(e) => setCrossingHubId(e.target.value)}>
              <option value="">All crossing hubs</option>
              {renderHubOptions()}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Via stop
            </label>
            <select value={viaHubId} onChange={(e) => setViaHubId(e.target.value)}>
              <option value="">All via stops</option>
              {renderHubOptions()}
            </select>
          </div>
        </ListFilterPanel>

        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading routes..." />
          </div>
        ) : routes.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No routes found</div>
        ) : (
          <>
            {/* Table - Desktop View */}
            <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
              <div className="w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[720px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700 border-b">
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">ROUTE ID</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">NAME</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold">PATH</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">CROSSING LOCATION</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">VIA STOPS</th>
                      <th className="px-4 py-3 text-left text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {routes.map((route) => (
                      <tr key={route.route_id} className="hover:bg-gray-50 transition-colors relative">
                        <td className="px-4 py-3 whitespace-nowrap align-top">
                          <button
                            onClick={() => navigate(`/routes/view/${route.route_id}`)}
                            className={listIdLinkClass}
                          >
                            {route.route_id}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap align-top">
                          {route.name || '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-sm align-top min-w-[160px] max-w-xs">
                          <RoutePathCell
                            path={formatPath(route)}
                            onViewFull={() => setViewPathRoute(route)}
                          />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <CrossingLocationCell route={route} />
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap align-top">
                          {(route.via_location_ids || []).length}
                        </td>
                        <td className="px-4 py-3 relative whitespace-nowrap align-top">
                          <RowActionsMenu
                            isOpen={activeDropdown === route.route_id}
                            onToggle={() =>
                              setActiveDropdown(activeDropdown === route.route_id ? null : route.route_id)
                            }
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => navigate(`/routes/view/${route.route_id}`)}
                            onEdit={() => navigate(`/routes/edit/${route.route_id}`)}
                            onDelete={() => handleDelete(route.route_id, route.name || route.route_id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>

            {/* Mobile Card View */}
            <div className="xl:hidden space-y-4">
              {routes.map((route) => (
                <div
                  key={route.route_id}
                  className="bg-white rounded-xl shadow p-4"
                >
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => navigate(`/routes/view/${route.route_id}`)}
                        className={`${listIdLinkMobileClass} break-all text-left`}
                      >
                        {route.route_id}
                      </button>
                      <p className="text-sm text-gray-800 mt-1 font-medium break-words">
                        {route.name || '—'}
                      </p>
                    </div>
                    <RowActionsMenu
                      isOpen={activeDropdown === route.route_id}
                      onToggle={() =>
                        setActiveDropdown(activeDropdown === route.route_id ? null : route.route_id)
                      }
                      onClose={() => setActiveDropdown(null)}
                      menuRef={dropdownRef}
                      onView={() => navigate(`/routes/view/${route.route_id}`)}
                      onEdit={() => navigate(`/routes/edit/${route.route_id}`)}
                      onDelete={() => handleDelete(route.route_id, route.name || route.route_id)}
                    />
                  </div>
                  <div className="space-y-2 text-xs sm:text-sm border-t border-gray-100 pt-3">
                    <div>
                      <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                        Path
                      </p>
                      <RoutePathCell
                        path={formatPath(route)}
                        onViewFull={() => setViewPathRoute(route)}
                        singleLine
                      />
                    </div>
                    <div>
                      <p className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wide mb-0.5">
                        Crossing location
                      </p>
                      <CrossingLocationCell route={route} />
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-gray-500">Via stops</span>
                      <span className="font-semibold text-gray-800">
                        {(route.via_location_ids || []).length}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

            </div>

            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onNext={() => setCurrentPage((page) => page + 1)}
            />
          </>
        )}
      </div>

      {viewPathRoute && (
        <RouteTrackModal route={viewPathRoute} onClose={() => setViewPathRoute(null)} />
      )}
    </DashboardLayout>
  );
};

export default RouteList;
